import React, {
  useState,
  useRef,
  Fragment,
} from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';
import constants from '../../constants';
import Logo from '../Logo';
import Container from '../Container';
import Nav from './Nav';
import ToggleBtn from './ToggleBtn';
import HeaderSearchForm from '../SearchForm/headerSearch';
import useOnClickOutside from './OnClickOutside';
import CallToAction from '../CallToAction';
import SignUpModal from '../SignUpModal';
import {
  isHelpCenterSection,
  isBlogSection,
  isAuthorSection,
  isTrialSection,
} from '../../utils/pageURLCheck';
import {
  NavBarWrapper,
  NavBrand,
  NavWrapper,
  NavControls,
  Progress,
  NavLinkExternal,
  PromotionBanner,
  Badge,
} from './HeaderStyles';

const NavBar = ({
  scrollPercentage,
  useDarkBackground,
  children,
  mobile,
  isOpen,
  searchOpen,
  pathname,
}) => (
  <NavBarWrapper mobile={mobile} useDarkBackground={useDarkBackground}>
    {/* <PromotionBanner>
      <span><Badge>30% off</Badge></span>
      <span className="hide-xs">Cyber Monday Sale</span>
      <Link href="https://l.workast.io/web-cyber-monday" target="_blank" rel="noopener noreferrer">Get the offer</Link>
    </PromotionBanner> */}
    <Container>
      <NavWrapper>
        <NavBrand useDarkBackground={useDarkBackground} isOpen={isOpen} searchOpen={searchOpen}>
          <Link to="/">
            <Logo />
            <b>Workast</b>
          </Link>
          {isBlogSection(pathname) || isAuthorSection(pathname) ? (<Link to="/blog/">&nbsp;| Blog </Link>) : (<></>)}
          {isHelpCenterSection(pathname) ? (<Link to="/help/">&nbsp;| Help Center </Link>) : (<></>)}
          {isTrialSection(pathname) ? (<a href="#top">&nbsp;| Start a trial </a>) : (<></>)}
        </NavBrand>
        {children}
      </NavWrapper>
    </Container>
    <Progress scrollPercentage={`${scrollPercentage}%`} />
  </NavBarWrapper>
);
NavBar.defaultProps = {
  scrollPercentage: false,
  useDarkBackground: false,
  mobile: false,
  isOpen: false,
  searchOpen: false,
  children: null,
};
NavBar.propTypes = {
  pathname: PropTypes.string.isRequired,
  useDarkBackground: PropTypes.bool,
  mobile: PropTypes.bool,
  isOpen: PropTypes.bool,
  searchOpen: PropTypes.bool,
  scrollPercentage: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  children: PropTypes.node,
};


const getRightContent = (pathname) => {
  if (isTrialSection(pathname)) {
    return (
      <NavControls>
        <ul>
          <li>
            <NavLinkExternal
              rel="noopener noreferrer"
              href="/2/upgrade"
            >
              Compare plans
            </NavLinkExternal>
          </li>
          <li>
            <CallToAction
              text={constants.TRIAL.LINK_TEXT_HEADER}
              link={constants.TRIAL.LINK_URL}
            />
          </li>
        </ul>
      </NavControls>
    );
  }

  return (
    <NavControls>
      <ul>
        <li>
          <NavLinkExternal
            target="_blank"
            rel="noopener noreferrer"
            href="https://my.workast.com"
          >
            Log In
          </NavLinkExternal>
        </li>
        <li>
          <SignUpModal buttonText="Get started" />
        </li>
      </ul>
    </NavControls>
  );
};

export const DesktopNavigation = ({
  scrollPercentage,
  useDarkBackground,
  menu,
  pathname,
  title,
}) => {
  const ref = useRef();
  const [searchOpen, setSearchOpen] = useState(false);
  useOnClickOutside(ref, () => {
    setTimeout(() => {
      setSearchOpen(false);
    }, 200);
  });

  return (
    <NavBar
      scrollPercentage={scrollPercentage}
      useDarkBackground={useDarkBackground}
      pathname={pathname}
    >
      {menu && (
        <Nav
          searchOpen={searchOpen}
          useDarkBackground={useDarkBackground}
          menu={menu}
          pathname={pathname}
          title={title}
        />
      )}
      <div ref={ref}>
        {isHelpCenterSection(pathname) ? (
          <HeaderSearchForm
            useDarkBackground={useDarkBackground}
            open={searchOpen}
            setOpen={setSearchOpen}
          />
        ) : (
          <></>
        )}
      </div>
      {getRightContent(pathname)}
    </NavBar>
  );
};
DesktopNavigation.defaultProps = {
  useDarkBackground: false,
  scrollPercentage: false,
  menu: null,
  title: '',
};
DesktopNavigation.propTypes = {
  pathname: PropTypes.string.isRequired,
  useDarkBackground: PropTypes.bool,
  scrollPercentage: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  menu: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string,
      name: PropTypes.string,
    })
  ),
  title: PropTypes.string,
};


export const MobileNavigation = ({
  useDarkBackground,
  menu,
  scrollPercentage,
  pathname,
  hideMenuButton,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen(!isOpen);
  const ref = useRef();
  const [searchOpen, setSearchOpen] = useState(false);
  useOnClickOutside(ref, () => {
    setTimeout(() => {
      setSearchOpen(false);
    }, 200);
  });

  const menuContent = !hideMenuButton
    ? (
      <Fragment>
        <Nav
          isOpen={isOpen}
          searchOpen={searchOpen}
          useDarkBackground={useDarkBackground}
          menu={menu}
          pathname={pathname}
        >
          <ul className="last-items">
            <li>
              <NavLinkExternal
                target="_blank"
                rel="noopener noreferrer"
                href="https://my.workast.com"
              >
                Log In
              </NavLinkExternal>
            </li>
            <li>
              <SignUpModal buttonText="Get started" />
            </li>
          </ul>
        </Nav>
        <div ref={ref}>
          {isHelpCenterSection(pathname) ? (
            <HeaderSearchForm
              open={searchOpen}
              setOpen={setSearchOpen}
              toggleOpen={isOpen}
              useDarkBackground={useDarkBackground}
            />
          ) : (
            <></>
          )}
        </div>
        <ToggleBtn toggle={toggle} isOpen={isOpen} useDarkBackground={useDarkBackground} />
      </Fragment>
    )
    : null;

  return (
    <NavBar
      scrollPercentage={scrollPercentage}
      searchOpen={searchOpen}
      pathname={pathname}
      isOpen={isOpen}
      useDarkBackground={useDarkBackground}
      mobile
    >
      {menuContent}
    </NavBar>
  );
};
MobileNavigation.defaultProps = {
  useDarkBackground: false,
  scrollPercentage: false,
  menu: null,
  hideMenuButton: false,
};
MobileNavigation.propTypes = {
  pathname: PropTypes.string.isRequired,
  useDarkBackground: PropTypes.bool,
  scrollPercentage: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  menu: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string,
      name: PropTypes.string,
    })
  ),
  hideMenuButton: PropTypes.bool,
};


export default { DesktopNavigation, MobileNavigation };
